import React, { useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import ColumnsPicker, { useVisibleColumns, type ColumnDef } from './ColumnsPicker';
import { formatNumber, formatPercent, formatDurationMinutes } from './chartUtils';

export interface FacilityPerformanceRow {
  facilityId: number;
  facilityName: string;
  city: string;
  facilityType: string;
  status: 'Active' | 'Inactive';
  studies: number;
  pending: number;
  adoptionPercent: number;
  avgTatMinutes: number | null;
  slaPercent: number | null;
  utilizationPercent: number | null;
}

const FACILITY_COLUMNS: ColumnDef<FacilityPerformanceRow>[] = [
  { key: 'facility', label: 'Facility' },
  { key: 'status', label: 'Status' },
  { key: 'studies', label: 'Studies' },
  { key: 'pending', label: 'Pending' },
  { key: 'adoption', label: 'Adoption' },
  { key: 'avgTat', label: 'Avg TAT' },
  { key: 'sla', label: 'SLA' },
  { key: 'utilization', label: 'Utilization' },
];

interface FacilityPerformanceTableProps {
  rows: FacilityPerformanceRow[];
  onRowClick?: (facilityId: number) => void;
}

export default function FacilityPerformanceTable({
  rows,
  onRowClick,
}: Readonly<FacilityPerformanceTableProps>) {
  const { visibleColumns, visibleKeys, toggleColumn } = useVisibleColumns(
    FACILITY_COLUMNS,
    'analytics-facility-columns-v2'
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const renderCell = (key: string, row: FacilityPerformanceRow) => {
    switch (key) {
      case 'facility':
        return (
          <>
            <div className="analytics-facility-name">{row.facilityName}</div>
            <div className="analytics-facility-meta">
              {row.city} · {row.facilityType}
            </div>
          </>
        );
      case 'status':
        return (
          <span
            className={`analytics-status-badge ${
              row.status === 'Active'
                ? 'analytics-status-badge--active'
                : 'analytics-status-badge--inactive'
            }`}
          >
            {row.status}
          </span>
        );
      case 'studies':
        return formatNumber(row.studies ?? 0);
      case 'pending':
        return formatNumber(row.pending ?? 0);
      case 'adoption':
        return formatPercent(row.adoptionPercent ?? 0);
      case 'avgTat':
        return row.avgTatMinutes != null ? (
          row.avgTatMinutes > 120 ? (
            <span className="analytics-tat-exceeded">
              {formatDurationMinutes(row.avgTatMinutes)}
            </span>
          ) : (
            formatDurationMinutes(row.avgTatMinutes)
          )
        ) : (
          '—'
        );
      case 'sla':
        return row.slaPercent != null ? formatPercent(row.slaPercent) : '—';
      case 'utilization':
        return row.utilizationPercent != null ? formatPercent(row.utilizationPercent) : '—';
      default:
        return '—';
    }
  };

  return (
    <section className="analytics-section-card">
      <div className="analytics-section-header">
        <h3 className="analytics-section-title">
          <Building2 size={18} />
          Facility-wise performance
        </h3>
        <ColumnsPicker
          columns={FACILITY_COLUMNS}
          storageKey="analytics-facility-columns-v2"
          visibleKeys={visibleKeys}
          toggleColumn={toggleColumn}
        />
      </div>
      {rows.length === 0 ? (
        <p className="analytics-empty">No facilities onboarded.</p>
      ) : (
        <>
          <div className="analytics-table-wrap">
            <table className="analytics-table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map(row => (
                  <tr
                    key={row.facilityId}
                    style={{ cursor: onRowClick ? 'pointer' : undefined }}
                    onClick={() => onRowClick?.(row.facilityId)}
                  >
                    {visibleColumns.map(col => (
                      <td key={col.key}>{renderCell(col.key, row)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="analytics-table-footer">
            <label className="analytics-table-page-size">
              Rows per page
              <select
                className="analytics-select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50].map(n => (
                  <option
                    key={n}
                    value={n}
                  >
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <span>
              Page {currentPage} of {totalPages} ({rows.length} facilities)
            </span>
            <div className="analytics-table-pagination">
              <button
                type="button"
                className="analytics-secondary-btn"
                disabled={currentPage <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className="analytics-secondary-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
